import type { OpaqueCursorPagination } from '../../authorization.types';
import type { SupportedIpgSystemId } from '../third-party-system.enums';

export const TerminalCommandReadinessVersion = 1 as const;

export const TerminalCommandReadinessResourceKind = {
    LOGICAL_TERMINAL: 'logical_terminal',
    TERMINAL_DEVICE: 'terminal_device',
} as const;

export type TerminalCommandReadinessResourceKind =
    (typeof TerminalCommandReadinessResourceKind)[keyof typeof TerminalCommandReadinessResourceKind];

export const TerminalCommandReadinessRequestOperation = {
    ONE_RESOURCE: 'one_resource',
    CHECKLIST_PAGE: 'checklist_page',
} as const;

export type TerminalCommandReadinessRequestOperation =
    (typeof TerminalCommandReadinessRequestOperation)[keyof typeof TerminalCommandReadinessRequestOperation];

interface TerminalCommandReadinessRequestBaseV1 {
    readonly terminal_command_readiness_version: typeof TerminalCommandReadinessVersion;
    readonly resource_kind: TerminalCommandReadinessResourceKind;
}

export interface TerminalCommandReadinessOneResourceRequestV1 extends TerminalCommandReadinessRequestBaseV1 {
    readonly operation: typeof TerminalCommandReadinessRequestOperation.ONE_RESOURCE;
    readonly resource_uuid: string;
}

export interface TerminalCommandReadinessChecklistPageRequestV1 extends TerminalCommandReadinessRequestBaseV1 {
    readonly operation: typeof TerminalCommandReadinessRequestOperation.CHECKLIST_PAGE;
    readonly organization_uuid: string;
    readonly cursor?: string;
    readonly limit?: number;
}

// `operation` identifies the exact request variant at every dispatch site.
export type TerminalCommandReadinessRequestV1 =
    | TerminalCommandReadinessOneResourceRequestV1
    | TerminalCommandReadinessChecklistPageRequestV1;

export const TerminalCommandReadinessStatus = {
    COMMAND_ACCEPTED: 'COMMAND_ACCEPTED',
    PROVIDER_REPORTED_ONLINE: 'PROVIDER_REPORTED_ONLINE',
    OFFLINE: 'OFFLINE',
    UNAVAILABLE: 'UNAVAILABLE',
    AUTHENTICATION_REJECTED: 'AUTHENTICATION_REJECTED',
    TIMEOUT: 'TIMEOUT',
    MALFORMED_RESPONSE: 'MALFORMED_RESPONSE',
    INVALID_CONFIGURATION: 'INVALID_CONFIGURATION',
    NOT_APPLICABLE: 'NOT_APPLICABLE',
} as const;

export type TerminalCommandReadinessStatus =
    (typeof TerminalCommandReadinessStatus)[keyof typeof TerminalCommandReadinessStatus];

interface TerminalCommandReadinessResourceSummaryBaseV1 {
    readonly resource_uuid: string;
    readonly display_name: string;
    readonly ipg_id: SupportedIpgSystemId;
}

export interface TerminalCommandReadinessLogicalTerminalSummaryV1 extends TerminalCommandReadinessResourceSummaryBaseV1 {
    readonly resource_kind: typeof TerminalCommandReadinessResourceKind.LOGICAL_TERMINAL;
}

export interface TerminalCommandReadinessTerminalDeviceSummaryV1 extends TerminalCommandReadinessResourceSummaryBaseV1 {
    readonly resource_kind: typeof TerminalCommandReadinessResourceKind.TERMINAL_DEVICE;
    readonly is_virtual: boolean;
}

// `resource_kind` identifies the resource-summary variant at every dispatch site.
export type TerminalCommandReadinessResourceSummaryV1 =
    | TerminalCommandReadinessLogicalTerminalSummaryV1
    | TerminalCommandReadinessTerminalDeviceSummaryV1;

export type TerminalCommandReadinessInventoryObservationV1 =
    | { readonly found: false }
    | { readonly found: true; readonly online: boolean };

// `ipg_id` confines provider-specific observations to this exhaustive subtree.
export type TerminalCommandReadinessDetailsV1 =
    | {
          readonly ipg_id: 1;
          readonly single_mid_inventory: TerminalCommandReadinessInventoryObservationV1;
          readonly multi_mid_inventory: TerminalCommandReadinessInventoryObservationV1;
      }
    | { readonly ipg_id: 2 }
    | { readonly ipg_id: 3 };

export interface TerminalCommandReadinessResultV1 {
    readonly terminal_command_readiness_version: typeof TerminalCommandReadinessVersion;
    readonly resource: TerminalCommandReadinessResourceSummaryV1;
    readonly status: TerminalCommandReadinessStatus;
    readonly message: string;
    readonly observed_at: string;
    readonly merchant_timezone: string;
    readonly details: TerminalCommandReadinessDetailsV1;
}

export interface TerminalCommandReadinessPageV1 {
    readonly terminal_command_readiness_version: typeof TerminalCommandReadinessVersion;
    readonly results: readonly TerminalCommandReadinessResultV1[];
    readonly pagination: OpaqueCursorPagination;
}
